import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from '../components/ui/SignaturePad';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, FileText, RefreshCw, Lock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DirectContractSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('read'); // read | sign | done
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [ort, setOrt] = useState('');
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  // PIN-Gate state
  const [pinVerified, setPinVerified] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinChecking, setPinChecking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contracts/direct/${token}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => {
        setError(e.response?.data?.detail || 'Vertrag nicht gefunden');
        setLoading(false);
      });
  }, [token]);

  const handlePinVerify = async () => {
    if (!pinInput.trim()) { setPinError('Bitte PIN eingeben'); return; }
    setPinChecking(true);
    setPinError('');
    try {
      const res = await axios.post(`${API}/contracts/direct/${token}/verify-pin`, { pin: pinInput.trim() });
      if (res.data.verified) {
        setPinVerified(true);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      // "Not Found" = FastAPI Standard-404 → Endpoint existiert nicht (älteres Backend)
      // In diesem Fall: PIN-Sperre umgehen, Vertrag öffnen
      if (detail === 'Not Found') {
        setPinVerified(true);
        return;
      }
      setPinError(detail || 'Falscher PIN');
    } finally { setPinChecking(false); }
  };

  // Wenn Vertrag geladen → prüfen ob PIN überhaupt erforderlich ist
  useEffect(() => {
    if (!data) return;
    // Stille Prüfung: leeren String → no_pin_required ODER Endpoint fehlt (älteres Backend)
    axios.post(`${API}/contracts/direct/${token}/verify-pin`, { pin: '' })
      .then(res => { if (res.data?.no_pin_required) setPinVerified(true); })
      .catch(err => {
        // Wenn Endpoint nicht existiert (FastAPI Standard-404) → auto-bypass
        if (err.response?.data?.detail === 'Not Found') {
          setPinVerified(true);
        }
        // Anderer Fehler = PIN nötig → Gate bleibt stehen
      });
  }, [data, token]);

  const handleSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Bitte zuerst unterschreiben');
      return;
    }
    setSaving(true);
    try {
      const sig = sigRef.current.toDataURL('image/png');
      await axios.post(`${API}/contracts/direct/${token}/sign`, {
        signature_an: sig,
        datum: datum.split('-').reverse().join('.'),
        ort,
      });
      setStep('done');
      toast.success('Vertrag erfolgreich unterzeichnet');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center space-y-2">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground/40" />
        <p className="text-lg font-semibold">{error}</p>
        <p className="text-sm text-muted-foreground">Der Link ist ungültig oder abgelaufen.</p>
      </div>
    </div>
  );

  if (step === 'done' || (data?.status === 'complete' && data?.signature_an && data?.signature_an !== 'already_signed')) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-green-50 dark:bg-green-950">
      <div className="text-center space-y-3 max-w-sm">
        <CheckCircle2 className="h-16 w-16 mx-auto text-green-500" />
        <h1 className="text-xl font-bold text-foreground">Erfolgreich unterzeichnet</h1>
        <p className="text-sm text-muted-foreground">
          Dein Vertrag wurde digital gespeichert. Du erhältst in Kürze eine E-Mail mit deiner verschlüsselten Vertragskopie.
        </p>
      </div>
    </div>
  );

  // ── PIN-Gate ──────────────────────────────────────────────────────────────
  if (!pinVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm">
          <div className="bg-card rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Lock className="h-8 w-8" />
              </div>
              <h1 className="text-xl font-bold">Sicherer Zugang</h1>
              <p className="text-sm opacity-80 mt-1">{data?.template_name || 'Arbeitsvertrag'}</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Bitte gib deinen persönlichen PIN-Code ein, den du von deinem Arbeitgeber erhalten hast.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">PIN-Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={pinInput}
                  onChange={e => { setPinInput(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handlePinVerify()}
                  placeholder="000000"
                  className={`w-full h-12 rounded-xl border text-center text-2xl font-bold tracking-[0.5em]
                    focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors
                    ${pinError ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-border bg-background'}`}
                  data-testid="contract-pin-input"
                  autoFocus
                />
                {pinError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" /> {pinError}
                  </p>
                )}
              </div>
              <Button onClick={handlePinVerify} disabled={pinChecking || pinInput.length < 1}
                className="w-full h-12 text-base" data-testid="contract-pin-submit">
                {pinChecking ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Vertrag öffnen →'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <FileText className="h-8 w-8 mx-auto text-primary" />
          <h1 className="text-xl font-bold">{data.template_name}</h1>
          <p className="text-sm text-muted-foreground">Hallo {data.employee_name} — bitte lies den Vertrag und unterschreibe digital.</p>
        </div>

        {/* Contract text */}
        <div className="border rounded-xl p-4 bg-muted/20 max-h-[50vh] overflow-y-auto">
          {data.document_type === 'rv_befreiung' ? (
            <div
              className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: data.text_snapshot }}
            />
          ) : (
            <pre className="text-xs leading-relaxed whitespace-pre-wrap font-sans text-foreground">{data.text_snapshot}</pre>
          )}
        </div>

        {step === 'read' && (
          <Button className="w-full" onClick={() => setStep('sign')}>
            Ich habe den Vertrag gelesen — Weiter zur Unterschrift
          </Button>
        )}

        {step === 'sign' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Datum</Label>
                <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} className="h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Ort</Label>
                <Input value={ort} onChange={e => setOrt(e.target.value)} placeholder="z.B. Stuttgart" className="h-9" />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Unterschrift (mit Finger/Stift zeichnen)</Label>
                <Button variant="ghost" size="sm" onClick={() => sigRef.current?.clear()} className="h-7 text-xs gap-1">
                  <RefreshCw className="h-3 w-3" /> Löschen
                </Button>
              </div>
              <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-zinc-900">
                <SignaturePad ref={sigRef} penColor="#111" height={180} />
              </div>
              <p className="text-xs text-muted-foreground text-center">Bitte mit Finger oder Stift unterschreiben</p>
            </div>

            <Button className="w-full" onClick={handleSign} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gespeichert…</> : 'Vertrag jetzt unterzeichnen'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
